import { useState, useEffect, useMemo } from "react";
import { Box, Alert, CircularProgress, Button, Divider } from "@mui/material";

import { FieldControl } from "../../../shared/components";

import { useDisplayField } from "../../../hooks/useDisplayField";
import { usePhase } from "../../projectOverview/hooks/usePhase";
import { useGetResourceReqDetails } from "../hooks/useGetResourceReqDetails";

import { useCreateResourceRequest, useGetActivity, useUpdateResourceRequest } from "../mutations";
import { useGetJeopardyStakeholders } from "../../../shared/hooks";

interface AddRequestProps {
    projectId: string | undefined;
    requestId: string | null;
    isEdit: boolean;
    onSuccess: () => void;
    onClose: () => void;
    refetch: () => void;
}

export const AddRequest = ({ projectId, requestId, isEdit = false, onSuccess, onClose, refetch }: AddRequestProps) => {

    const { getFilterOptions } = useDisplayField([]);

    const phaseData = usePhase();
    const activityData = useGetActivity();

    const createMutation = useCreateResourceRequest();
    const updateMutation = useUpdateResourceRequest();

    const [requestData, setRequestData] = useState<{
        type: string | null;
        description: string | null;
        duration: number;
        activity_id: string | number | null;
        supplier: string | null;
        status: string | null;
        comments: string | null;
        phase_id: string | number | null;
        owner_of_request?: number | null;
        required_date: string | null;
        requested_date: string | null;
        confirmed_date: string | null;
    }>({
        type: null,
        description: null,
        duration: 0,
        activity_id: null,
        supplier: null,
        status: null,
        comments: null,
        phase_id: null,
        owner_of_request: null,
        required_date: null,
        requested_date: null,
        confirmed_date: null,
    });

    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);

    const {
        data,
        isFetching: isResourceReqFetching, isPending: isResourceReqPending, isError: isResourceReqError, refetch: refetchDetails
    } = useGetResourceReqDetails({ projectId, requestId });
    const { data: stakeHolderData, isLoading, } = useGetJeopardyStakeholders({ params: { project_id: projectId } });
    
    const userOptions = useMemo(() => {
        return stakeHolderData?.data?.flatMap((role: any) =>
            role?.users?.map((user: any) => ({
                value: user.user_id,
                label: user.user_name,
            }))
        ) || [];
    }, [stakeHolderData]);

    const handleChange = (key: string, value: any) => {
        setRequestData((prevData) => ({
            ...prevData,
            [key]: value,
        }));
        if (error) setError(null);
        // Clear validation error for this field when user makes changes
        if (validationErrors[key]) {
            setValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[key];
                return newErrors;
            });
        }
    };

    const validateForm = () => {
        const errors: Record<string, string> = {};

        if (!requestData.type) {
            errors.type = "Type is required";
        }
        if (!requestData.description) {
            errors.description = "Description is required";
        }
        if (!requestData.duration || requestData.duration <= 0) {
            errors.duration = "Duration must be greater than 0";
        }
        if (!requestData.required_date) {
            errors.required_date = "Required date is required";
        }
        if (!requestData.requested_date) {
            errors.requested_date = "Requested date is required";
        }
        if (!requestData.supplier) {
            errors.supplier = "Supplier is required";
        }
        if (!requestData.phase_id) {
            errors.phase_id = "Phase is required";
        }
        if (!requestData.activity_id) {
            errors.activity_id = "Activity is required";
        }
        if (!requestData.status) {
            errors.status = "Status is required";
        }
        if (!requestData.owner_of_request) {
            errors.owner_of_request = "Owner of request is required";
        }
        if (!requestData.comments) {
            errors.comments = "Comments are required";
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            setError("Please fill in all required fields correctly");
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const payload = {
                "project_id": projectId,
                "phase_id": requestData.phase_id,
                "activity_id": requestData.activity_id,
                "resource_type": requestData.type,
                "description": requestData.description,
                "duration": requestData.duration,
                "required_date": requestData.required_date,
                "requested_date": requestData.requested_date,
                "confirmed_date": requestData.confirmed_date,
                "status": requestData.status,
                "resource_supplier": requestData.supplier,
                "owner_of_request": requestData.owner_of_request,
                "comments": requestData.comments,
            }
            if (isEdit && requestId) {
                await updateMutation.mutateAsync({ id: requestId, payload });
                refetchDetails?.();
            } else {
                await createMutation.mutateAsync({ payload });
            }
            refetch?.();
            onSuccess?.();
            onClose?.();
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : `Failed to ${isEdit ? 'update' : 'create'} resource request`;
            setError(errorMessage);
            console.error(`Failed to ${isEdit ? 'update' : 'create'} resource request:`, err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (requestData.phase_id) {
            activityData.mutateAsync({ projectId: projectId, phaseId: String(requestData.phase_id) });
        };
    }, [requestData.phase_id]);

    useEffect(() => {
        setInitialDataLoaded(false);
        if (!isEdit) {
            setRequestData({
                type: null,
                description: null,
                duration: 0,
                activity_id: null,
                supplier: null,
                status: null,
                comments: null,
                phase_id: null,
                owner_of_request: null,
                required_date: null,
                requested_date: null,
                confirmed_date: null,
            });
        }
    }, [isEdit, requestId]);

    useEffect(() => {
        if (isEdit && requestId && data && !isResourceReqFetching && !isResourceReqPending && !isResourceReqError && phaseData.data && !initialDataLoaded) {
            setRequestData({
                type: data.resource_type,
                description: data.description,
                duration: parseFloat(data.duration) || 0,
                supplier: data.resource_supplier,
                status: data.status,
                comments: data.comments,
                activity_id: Number(data.activity_id),
                phase_id: Number(data.phase_id),
                required_date: data.required_date,
                requested_date: data.requested_date,
                confirmed_date: data.confirmed_date,
                owner_of_request: Number(data.owner_of_request),
            });
            setInitialDataLoaded(true);
        };
    }, [isEdit, requestId, data, isResourceReqFetching, isResourceReqPending, isResourceReqError, phaseData.data, initialDataLoaded]);

    useEffect(() => {
        phaseData.mutateAsync(projectId);
    }, []);

    if ((isEdit && (isResourceReqFetching || isResourceReqPending))) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>

            {error && (
                <Box sx={{ px: 2, pt: 2 }}>
                    <Alert severity="error" onClose={() => setError(null)}>
                        {error}
                    </Alert>
                </Box>
            )}

            <Box sx={{ px: 2, py: 2, flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>

                <FieldControl
                    keyName={"type"}
                    label={"Type"}
                    variant={"select"}
                    value={requestData.type}
                    options={getFilterOptions("RESOURCE_TYPE").map((option) => ({ label: option.display_text, value: option.key }))}
                    onChange={(_key: string, value: string) => handleChange("type", value)}
                    disabled={loading}
                    required
                    error={!!validationErrors.type}
                    helperText={validationErrors.type}
                />

                <FieldControl
                    keyName={"description"}
                    label={"Description"}
                    variant={"input"}
                    value={requestData.description}
                    onChange={(_key: string, value: string) => handleChange("description", value)}
                    disabled={loading}
                    required
                    error={!!validationErrors.description}
                    helperText={validationErrors.description}
                />

                <FieldControl
                    keyName={"duration"}
                    label={"Duration (in days)"}
                    variant={"input"}
                    value={requestData.duration.toString()}
                    onChange={(_key: string, value: string) => handleChange("duration", parseFloat(value) || 0)}
                    disabled={loading}
                    required
                    error={!!validationErrors.duration}
                    helperText={validationErrors.duration}
                />

                <FieldControl
                    keyName={"required_date"}
                    label={"Required Date"}
                    variant={"date"}
                    value={requestData.required_date}
                    onChange={(_key: string, value: string) => handleChange("required_date", value)}
                    disabled={loading}
                    required
                    error={!!validationErrors.required_date}
                    helperText={validationErrors.required_date}
                />

                <FieldControl
                    keyName={"requested_date"}
                    label={"Requested Date"}
                    variant={"date"}
                    value={requestData.requested_date}
                    onChange={(_key: string, value: string) => handleChange("requested_date", value)}
                    disabled={loading}
                    required
                    error={!!validationErrors.requested_date}
                    helperText={validationErrors.requested_date}
                />

                <FieldControl
                    keyName={"confirmed_date"}
                    label={"Confirmed Date"}
                    variant={"date"}
                    value={requestData.confirmed_date}
                    onChange={(_key: string, value: string) => handleChange("confirmed_date", value)}
                    disabled={loading}
                />

                <FieldControl
                    keyName={"supplier"}
                    label={"Supplier"}
                    variant={"select"}
                    value={requestData.supplier}
                    options={requestData.type ? getFilterOptions("RESOURCE_SUPPLIER").filter((m) => { return m.parent_key?.split(',').map((s: string) => s.trim()).includes(requestData.type) }).map((option) => ({ label: option.display_text, value: option.key })) : []}
                    onChange={(_key: string, value: string) => handleChange("supplier", value)}
                    disabled={loading}
                    required
                    error={!!validationErrors.supplier}
                    helperText={validationErrors.supplier}
                />

                <FieldControl
                    keyName={"phase_id"}
                    label={"Phase"}
                    variant={"select"}
                    value={requestData.phase_id}
                    options={phaseData.data?.data.map((phase: { id: string; name: string }) => ({ label: phase.name, value: phase.id })) || []}
                    onChange={(_key: string, value: string) => handleChange("phase_id", value)}
                    disabled={loading}
                    required
                    error={!!validationErrors.phase_id}
                    helperText={validationErrors.phase_id}
                />

                <FieldControl
                    keyName={"activity_id"}
                    label={"Activity"}
                    variant={"select"}
                    value={requestData.activity_id}
                    options={activityData?.data?.data?.activities.map((activity: { id: string; name: string }) => ({ label: activity.name, value: activity.id })) || []}
                    onChange={(_key: string, value: string) => handleChange("activity_id", value)}
                    disabled={loading}
                    required
                    error={!!validationErrors.activity_id}
                    helperText={validationErrors.activity_id}
                />

                <FieldControl
                    keyName={"status"}
                    label={"Status"}
                    variant={"select"}
                    value={requestData.status}
                    options={getFilterOptions("RESOURCE_STATUS").map((option) => ({ label: option.display_text, value: option.key }))}
                    onChange={(_key: string, value: string) => handleChange("status", value)}
                    disabled={loading}
                    required
                    error={!!validationErrors.status}
                    helperText={validationErrors.status}
                />

                <FieldControl
                    keyName="owner_of_request"
                    label="Owner of Request"
                    variant="select"
                    options={userOptions}
                    value={requestData.owner_of_request}
                    onChange={(_key: string, value: string) => handleChange("owner_of_request", value)}
                    disabled={loading}
                    required
                    error={!!validationErrors.owner_of_request}
                    helperText={validationErrors.owner_of_request}
                />

                <FieldControl
                    keyName={"comments"}
                    label={"Comments"}
                    variant={"input"}
                    value={requestData.comments}
                    onChange={(_key: string, value: string) => handleChange("comments", value)}
                    disabled={loading}
                    required
                    error={!!validationErrors.comments}
                    helperText={validationErrors.comments}
                />

            </Box>

            <Divider />

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2,
                    px: 3,
                    py: 2,
                }}
            >
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                    sx={{
                        minWidth: 150,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    {loading && <CircularProgress size={20} />}
                    {loading ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update Request' : 'Create Request')}
                </Button>
            </Box>

        </Box>
    );

};